import React, {
  useCallback,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { createContext, useContextSelector } from 'use-context-selector';
import {
  VERIFICATION_SUBMIT_MUTATION_KEY,
  VerificationCacheData,
  verificationQueryKey,
} from '../api/verifications/verificationCache';

// ECDEX-3840 (slice 4, MR 2): the verification the page is showing, for the
// verification components. They used to get it from `state.verifications`
// through `connectToVerifications`; they now read it here, and only this file
// knows where it comes from.
//
// MR 2b: the source is the query cache, not the Redux slice. `useVerification`
// tells this provider which verification the page is on, the fetch fills that
// cache entry, and a submit or an unsubscribe replaces its `verification` (see
// `useVerification`). `submitting` is whether the submit mutation is running.
//
// MR 3: `currentPage`, the wizard step, lives here too. It used to be
// `state.navigation.currentPage`. A fetch sets it back to the first step (see
// `useVerification`), as `FETCH_VERIFICATION_SUCCESS` did.
export interface VerificationContextValue {
  verification: Record<string, any> | null;
  steps: any[];
  expired: boolean;
  submitting: boolean;
  // The step of the wizard the candidate is on (an index into `steps`).
  currentPage: number;
}

const VerificationContext = createContext<VerificationContextValue>({
  verification: null,
  steps: [],
  expired: false,
  submitting: false,
  currentPage: 0,
});

interface VerificationActions {
  setVerificationId: (verificationId?: string) => void;
  setCurrentPage: (currentPage: number) => void;
}

const VerificationActionsContext = React.createContext<VerificationActions>({
  setVerificationId: () => {},
  setCurrentPage: () => {},
});

export const VerificationProvider: React.FC<{
  children?: React.ReactNode;
  // The verification to show before a page has asked for one (tests).
  initialVerificationId?: string;
  // The wizard step to start on (tests).
  initialCurrentPage?: number;
}> = ({ children, initialVerificationId, initialCurrentPage = 0 }) => {
  const queryClient = useQueryClient();
  const [verificationId, setVerificationId] = useState(initialVerificationId);
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);

  const cache = queryClient.getQueryCache();
  const getData = () =>
    queryClient.getQueryData<VerificationCacheData>(
      verificationQueryKey(verificationId),
    );
  // The static pages (scripts/ssg) render this provider on the server, which
  // needs a server snapshot; the cache is empty there.
  const data = useSyncExternalStore(
    useCallback(onChange => cache.subscribe(onChange), [cache]),
    getData,
    getData,
  );
  const submitting =
    useIsMutating({ mutationKey: VERIFICATION_SUBMIT_MUTATION_KEY }) > 0;

  const value = useMemo(
    () => ({
      verification: data?.verification ?? null,
      steps: data?.steps ?? [],
      expired: data?.expired ?? false,
      submitting,
      currentPage,
    }),
    [data, submitting, currentPage],
  );
  const actions = useMemo(() => ({ setVerificationId, setCurrentPage }), []);

  return (
    <VerificationActionsContext.Provider value={actions}>
      <VerificationContext.Provider value={value}>
        {children}
      </VerificationContext.Provider>
    </VerificationActionsContext.Provider>
  );
};

export const useVerificationValue = <T,>(
  select: (value: VerificationContextValue) => T,
): T => useContextSelector(VerificationContext, select);

export const useVerificationActions = () =>
  React.useContext(VerificationActionsContext);
